// Releases (Console D, APS-307): is this release better or worse, and why? The verdict as the
// meta, a rail of the newest releases (pick any two), one comparison grouped Stability,
// Performance and Issues where a change links to its cause, crash-free sessions by days since
// each release shipped, adoption over the window, the candidate's symbols, then every release in
// the window. A release's own page is /releases/$release (Release.tsx).
import {
  type QueryFunctionContext,
  type UseQueryResult,
  useQueries,
  useQuery,
} from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import {
  alertRulesQuery,
  appsQuery,
  appVitalsQuery,
  type DeviceParams,
  lastDays,
  type MissingSymbolsPage,
  missingSymbolsQuery,
  releaseDaysQuery,
  releasesQuery,
  usageQuery,
} from '../api/queries'
import type { App, ReleaseHealth } from '../api/types'
import { DebugOnlyLine } from '../components/DebugOnly'
import { CodeTabs, EmptyNote, Hbar, PageHead, Section, Status, Table } from '../components/d'
import { copyText, useToast } from '../components/d/Toast'
import { Icon } from '../components/icons'
import { LineChart, useWidth } from '../components/LineChart'
import { deviceMeta } from '../components/Perf'
import { CompareTable, useComparison } from '../components/ReleaseCompare'
import { RowKeysHint } from '../components/RowKeys'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { compact, duration, percentSpaced } from '../lib/format'
import { firingRules } from '../lib/overview'
import { RunQueue } from '../lib/panels'
import {
  adoption,
  adoptionByDay,
  busiestApp,
  cliCompare,
  compare,
  crashFreeSinceShip,
  pickPair,
  type RailState,
  railState,
  releasesNewestFirst,
  repick,
  versionBuild,
} from '../lib/releases'
import { useProjectParams } from './params'
import { KIND_KEY, shownKind, uploadTabs } from './SymbolsSettings'
import { deviceParams, type ReleasesListSearch } from './search'

export const WINDOW_DAYS = 30
/** The rail's releases, newest first (Console D: four cells). */
const RAIL = 4
/** Releases the chart since ship draws, newest first. */
const SINCE_SHIP = 3
/** Days the chart since ship spans. */
const SHIP_DAYS = 15

/** The window's reads share this: now to the minute, and midnight UTC 29 days before. */
export function releaseWindow(now: number): { from: string; to: string } {
  const d = new Date(now)
  const from = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - (WINDOW_DAYS - 1))
  return { from: new Date(from).toISOString(), to: new Date(now).toISOString() }
}

/**
 * The project's app releases in the window, the scope's app and its releases, newest first; with
 * `device` (the list applies the bar's device filters, a release's page does not), only what
 * those sessions count.
 */
export function useReleaseList(device: DeviceParams = {}) {
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as ReleasesListSearch
  // The window is fixed per minute so the query keys stay stable.
  const now = Math.floor(Date.now() / 60_000) * 60_000
  // Unfiltered, the key is the bar's own release list's: one request for both.
  const narrowed = !!device.device_model || !!device.os_version
  const all = useQuery(
    releasesQuery(projectId, { ...lastDays(WINDOW_DAYS, now), ...device }, [
      'window',
      WINDOW_DAYS,
      now,
      ...(narrowed ? [device] : []),
    ]),
  )
  const apps = useQuery(appsQuery(orgId, projectId))
  const appId = search.app ?? (all.data ? busiestApp(all.data) : undefined)
  const list = all.data ? releasesNewestFirst(all.data, appId) : []
  const app = apps.data?.find((a) => a.id === appId)
  // Adoption is the share of the last 24 hours, as the mock's rail and table say.
  const lastDay = lastDays(1, now)
  const recent = useQuery({
    ...releasesQuery(projectId, { ...lastDay, app_id: appId, ...device }, [
      'adoption',
      appId,
      now,
      device,
    ]),
    enabled: !!appId,
  })
  const share = new Map(adoption(recent.data ?? []).map((a) => [a.release, a.share]))
  return { all, apps, appId, app, list, now, search, share, recent }
}

/** The release health is an estimate when summaries were sampled over the fair-use limit (D110). */
export function useSampled(): number | undefined {
  const { orgId } = useProjectParams()
  const usage = useQuery(usageQuery(orgId))
  return usage.data?.fair_use?.restrictions.summary_rate ?? undefined
}

/** The alert's crash-free limit for the app, as a rate (0 to 1), when a rule has one. */
export function useCrashFreeLimit(appId: string | undefined): number | undefined {
  const { orgId, projectId } = useProjectParams()
  const rules = useQuery(alertRulesQuery(orgId, projectId))
  const rule = firingRules(rules.data?.rules)
    .concat((rules.data?.rules ?? []).filter((r) => r.enabled && r.state !== 'firing'))
    .find((r) => r.preset === 'crash_free_below' && (!r.app_id || r.app_id === appId))
  const limit = rule ? Number(rule.params.threshold_percent) / 100 : undefined
  return limit !== undefined && Number.isFinite(limit) ? limit : undefined
}

export function ReleasesPage() {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const device = deviceParams(useSearch({ strict: false }) as ReleasesListSearch)
  const { all, appId, app, list, now, search, share } = useReleaseList(device)
  const deviceText = deviceMeta(i18n, app, device)
  const sampled = useSampled()
  const android = app?.platform === 'android'
  const pair = pickPair(list, search.cand ?? search.release, search.base)
  const cand = pair.candidate
  const base = pair.base
  const cmp = useComparison({ projectId, appId, android, base, candidate: cand, device })
  // One accent per view: once j or k moves through All releases, the comparison gives it up.
  const [moved, setMoved] = useState(false)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest?.('input, textarea, select')
      if ((e.key === 'j' || e.key === 'k') && !typing && !e.metaKey && !e.ctrlKey && !e.altKey)
        setMoved(true)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])
  const { toast, show } = useToast()

  const params = { orgId, projectId }
  const scope = appId ? { app: appId } : {}

  // An empty list being read again (the project's first session just arrived, APS-348) is not
  // "No sessions yet." until the new answer says so.
  if (all.isPending || (all.isFetching && list.length === 0))
    return (
      <main id="main" className="pg rs-pg">
        <Loading />
      </main>
    )
  if (all.isError)
    return (
      <main id="main" className="pg rs-pg">
        <PageHead title={t('nav.releases')} />
        <ErrorState error={all.error} retry={() => all.refetch()} />
      </main>
    )
  if (!cand)
    return (
      <main id="main" className="pg rs-pg">
        <PageHead
          title={t('nav.releases')}
          meta={t('releases.kicker.none', { days: WINDOW_DAYS })}
        />
        <EmptyNote title={t('releases.empty.title')}>
          <p>{t('releases.empty.body')}</p>
          <DebugOnlyLine
            projectId={projectId}
            scope={{ app: search.app, days: WINDOW_DAYS }}
            empty
          />
          <Link
            to="/o/$orgId/p/$projectId/setup"
            params={params}
            search={scope}
            className="btn solid"
          >
            {t('releases.empty.action')}
          </Link>
        </EmptyNote>
      </main>
    )

  const verdict = compare(cand, base, cmp.change)
  const topNew = cmp.newIssues[0]
  const reasons = [
    cmp.newIssues.length > 0 ? t('releases.meta.new', { count: cmp.newIssues.length }) : '',
    cmp.regressedHere.length > 0
      ? t('releases.meta.regressed', { count: cmp.regressedHere.length })
      : '',
  ].filter(Boolean)
  const rail = list.slice(0, RAIL)
  const pick = (release: string) => {
    const next = repick(list, { candidate: cand.release, base: base?.release }, release)
    navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        cand: next.candidate,
        base: next.base,
        release: undefined,
      }),
    })
  }
  const releaseLink = (release: string) => ({
    to: '/o/$orgId/p/$projectId/releases/$release' as const,
    params: { ...params, release },
    search: scope,
  })

  return (
    <main id="main" className="pg rs-pg">
      <PageHead
        title={t('nav.releases')}
        meta={
          <>
            <span translate="no">
              {verdict.verdict === 'only'
                ? t('releases.meta.first', { release: cand.release })
                : t(`releases.meta.${verdict.verdict}`, {
                    release: cand.release,
                    previous: base?.release ?? '',
                  })}
            </span>
            {reasons.length > 0 ? `: ${reasons.join(', ')}` : ''}
            {sampled ? null : (
              <>
                {' · '}
                <span title={t('releases.meta.countedTitle')}>{t('releases.meta.counted')}</span>
              </>
            )}
            {deviceText ? (
              <>
                {' · '}
                <span translate="no">{deviceText}</span>
              </>
            ) : null}
          </>
        }
        actions={
          <>
            <Link className="btn" {...releaseLink(cand.release)}>
              {t('releases.open', { release: cand.release })}
            </Link>
            {topNew ? (
              <Link
                className="btn solid"
                to="/o/$orgId/p/$projectId/issues/$issueId"
                params={{ ...params, issueId: topNew.id }}
                search={scope}
              >
                {t('releases.openTopNew')}
              </Link>
            ) : null}
          </>
        }
      />

      {sampled ? (
        <p className="notice" role="status">
          {t('releases.estimate', { rate: sampled })}
        </p>
      ) : null}

      <fieldset className="rs-rail" aria-label={t('releases.rail.label')}>
        {rail.map((r, i) => (
          <RailCell
            key={r.release}
            r={r}
            state={railState(r, list[i + 1])}
            role={
              r.release === cand.release
                ? 'candidate'
                : r.release === base?.release
                  ? 'base'
                  : i === 0
                    ? 'latest'
                    : undefined
            }
            share={share.get(r.release)}
            onPick={() => pick(r.release)}
          />
        ))}
      </fieldset>

      <div className="grid2">
        <Section
          className="rs-cmpsec"
          title={
            <span translate="no">
              {base
                ? t('releases.compare.title', { candidate: cand.release, base: base.release })
                : cand.release}
            </span>
          }
          meta={t('releases.compare.meta', { days: WINDOW_DAYS })}
          right={
            base ? (
              <button
                type="button"
                className="btn sm"
                onClick={async () =>
                  show(
                    (await copyText(cliCompare(base.release, cand.release, appId)))
                      ? t('releases.cli.copied')
                      : t('d.code.copyFailed'),
                  )
                }
              >
                <Icon name="copy" className="se-i" />
                {t('releases.cli.copy')}
              </button>
            ) : undefined
          }
        >
          {!base ? (
            <p className="note">{t('releases.compare.first', { release: cand.release })}</p>
          ) : cmp.pending ? (
            <Loading />
          ) : (
            <CompareTable
              label={t('releases.compare.label')}
              base={base.release}
              candidate={cand.release}
              rows={cmp.rows}
              last={t('releases.col.why')}
              accent={moved ? undefined : 'cfs'}
            />
          )}
          <div className="t-foot">
            <Link className="ul" {...releaseLink(cand.release)}>
              {t('releases.open', { release: cand.release })}
            </Link>
          </div>
        </Section>
        <div className="stack">
          <SinceShip
            list={list.slice(0, SINCE_SHIP)}
            appId={appId}
            projectId={projectId}
            now={now}
            device={device}
          />
          <AdoptionCurve
            list={rail}
            appId={appId}
            projectId={projectId}
            now={now}
            device={device}
          />
          <SymbolsSummary projectId={projectId} release={cand.release} appId={appId} app={app} />
        </div>
      </div>

      <AllReleases list={list} appId={appId} android={android} share={share} device={device} />
      {toast}
    </main>
  )
}

const RAIL_STATUS: Record<RailState, 'reg' | 'res' | 'on'> = {
  worse: 'reg',
  better: 'res',
  same: 'res',
  first: 'on',
}

function RailCell({
  r,
  state,
  role,
  share,
  onPick,
}: {
  r: ReleaseHealth
  state: RailState
  role: 'candidate' | 'base' | 'latest' | undefined
  share: number | undefined
  onPick: () => void
}) {
  const i18n = useI18n()
  const { t, date } = i18n
  const picked = role === 'candidate' || role === 'base'
  return (
    <button type="button" className="rs-cell" aria-pressed={picked} onClick={onPick}>
      <span className="rs-in">
        <span className="rs-tag">{role ? t(`releases.rail.${role}`) : ''}</span>
        <span className="rs-r1">
          <span className="rs-v" translate="no">
            {r.release}
          </span>
          <Status s={RAIL_STATUS[state]} label={t(`releases.state.${state}`)} />
        </span>
        <span className="rs-sub">
          <span>{t('releases.shipped', { date: date(r.released_at) })}</span>
          <span className="rs-dt" aria-hidden="true">
            ·
          </span>
          <span>{t('releases.rail.sessions', { sessions: compact(i18n, r.sessions) })}</span>
        </span>
        <span className="rs-ad">
          <Hbar share={share ?? 0} />
          <span>{t('releases.rail.adoption', { share: percentSpaced(i18n, share ?? 0, 0) })}</span>
        </span>
        <span className="rs-cf">
          <b>
            {i18n.number(r.crash_free_sessions_rate * 100, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </b>
          {` % ${t('releases.rail.crashFree')}`}
        </span>
      </span>
    </button>
  )
}

/** The days of the window, read once per release and once for the app, shared by both charts. */
function useWindowDays(
  projectId: string,
  appId: string | undefined,
  releases: (string | undefined)[],
  now: number,
  device: DeviceParams,
) {
  const { from, to } = releaseWindow(now)
  return useQueries({
    queries: releases.map((r) => ({
      ...releaseDaysQuery(projectId, appId ?? '', r, from, to, device),
      enabled: !!appId,
    })),
  })
}

/** Crash-free sessions by days since each of the newest releases shipped, cumulative. */
function SinceShip({
  list,
  appId,
  projectId,
  now,
  device,
}: {
  list: ReleaseHealth[]
  appId: string | undefined
  projectId: string
  now: number
  device: DeviceParams
}) {
  const i18n = useI18n()
  const { t } = i18n
  const days = useWindowDays(
    projectId,
    appId,
    list.map((r) => r.release),
    now,
    device,
  )
  const limit = useCrashFreeLimit(appId)
  const series = list.map((r, i) =>
    crashFreeSinceShip(days[i]?.data ?? [], r.released_at, SHIP_DAYS),
  )
  const pct = (v: number) =>
    i18n.number(v * 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const last = (s: (number | null)[]) => s.findLast((v) => v !== null) ?? null
  const label = (i: number) =>
    i === 0
      ? t('releases.ship.day0')
      : i === SHIP_DAYS - 1
        ? t('releases.ship.dayN', { n: i })
        : String(i)
  const [first, ...rest] = list
  const firstSeries = series[0] ?? []
  const pad = (s: (number | null)[]) => Array.from({ length: SHIP_DAYS }, (_, i) => s[i] ?? null)
  const ends = list.map((r, i) => {
    const v = last(series[i] ?? [])
    return v === null ? undefined : `${versionBuild(r.release).version} ${pct(v)}`
  })
  return (
    <Section title={t('releases.ship.title')} meta={t('releases.ship.meta')}>
      {days.some((d) => d.isPending) ? (
        <Loading />
      ) : !first || firstSeries.every((v) => v === null) ? (
        <p className="note">{t('releases.chart.empty')}</p>
      ) : (
        <LineChart
          label={t('releases.ship.label', { release: first.release })}
          height={210}
          points={pad(firstSeries).map((value, i) => ({ label: label(i), value }))}
          format={pct}
          name={first.release}
          endsInGutter
          lastLabel={() => ends[0] ?? ''}
          more={rest.map((r, i) => ({
            name: r.release,
            values: pad(series[i + 1] ?? []),
            end: ends[i + 1],
          }))}
          threshold={
            limit !== undefined ? { value: limit, label: t('home.chart.alert') } : undefined
          }
        />
      )}
    </Section>
  )
}

/**
 * Each release's share of the app's sessions per day over the window, stacked in ink steps with
 * the newest at the bottom, its ship day dashed, and the last day's shares as end labels.
 */
function AdoptionCurve({
  list,
  appId,
  projectId,
  now,
  device,
}: {
  list: ReleaseHealth[]
  appId: string | undefined
  projectId: string
  now: number
  device: DeviceParams
}) {
  const i18n = useI18n()
  const { t, date } = i18n
  const reads = useWindowDays(
    projectId,
    appId,
    [undefined, ...list.map((r) => r.release)],
    now,
    device,
  )
  const [ref, width] = useWidth(360)
  const appDays = reads[0]?.data ?? []
  const pending = reads.some((d) => d.isPending)
  const shares = list.map((_, i) => adoptionByDay(reads[i + 1]?.data ?? [], appDays))
  const n = appDays.length
  const H = 200
  const L = 34
  const R = 66
  const T = 14
  const B = 24
  const W = width
  const x = (i: number) => L + (n > 1 ? (i / (n - 1)) * (W - L - R) : 0)
  const y = (v: number) => T + (1 - v) * (H - T - B)
  const base = Array.from({ length: n }, () => 0)
  const areas: { release: string; points: string; mid: number; end: number }[] = []
  shares.forEach((s, k) => {
    const top = base.map((b, i) => b + (s[i] ?? 0))
    const up = top.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    const down = base.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).reverse()
    areas.push({
      release: list[k]?.release ?? '',
      points: `${up.join(' ')} ${down.join(' ')}`,
      mid: y(((base[n - 1] ?? 0) + (top[n - 1] ?? 0)) / 2),
      end: s[n - 1] ?? 0,
    })
    top.forEach((v, i) => {
      base[i] = v
    })
  })
  // End labels pushed apart so they never overlap.
  const labels = areas.map((a) => ({ ...a, ly: a.mid })).sort((a, b) => a.ly - b.ly)
  labels.forEach((m, i) => {
    m.ly = Math.max(m.ly, i ? (labels[i - 1]?.ly ?? T) + 12 : T)
  })
  for (let i = labels.length - 1; i >= 0; i--) {
    const m = labels[i]
    if (m) m.ly = Math.min(m.ly, i === labels.length - 1 ? H - B : (labels[i + 1]?.ly ?? H) - 12)
  }
  const dayIndex = (iso: string) =>
    appDays.findIndex((d) => d.day.slice(0, 10) === iso.slice(0, 10))
  const markers = list.map((r) => ({ r, i: dayIndex(r.released_at) })).filter((m) => m.i > 0)
  const xLabels = n > 1 ? [0, Math.round((n - 1) / 3), Math.round(((n - 1) * 2) / 3), n - 1] : []
  const dayLabel = (i: number) =>
    appDays[i]
      ? date(`${appDays[i].day.slice(0, 10)}T00:00:00Z`, {
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        })
      : ''
  const empty = !pending && (n === 0 || appDays.every((d) => d.sessions === 0))
  return (
    <Section
      title={t('releases.adoption.title')}
      meta={t('releases.adoption.meta', { days: WINDOW_DAYS })}
    >
      <div ref={ref}>
        {pending ? (
          <Loading />
        ) : empty ? (
          <p className="note">{t('releases.chart.empty')}</p>
        ) : (
          <svg
            className="chart"
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            height={H}
            role="img"
            aria-label={t('releases.adoption.label', {
              shares: areas.map((a) => `${a.release} ${percentSpaced(i18n, a.end, 0)}`).join(', '),
            })}
          >
            {[0, 0.25, 0.5, 0.75, 1].map((v) => (
              <g key={v}>
                <line className="grid" x1={L} x2={W - R} y1={y(v)} y2={y(v)} />
                <text className="ax" x={L - 6} y={y(v) + 3.5} textAnchor="end">
                  {i18n.number(v * 100)}
                </text>
              </g>
            ))}
            {areas.map((a, k) => (
              <g key={a.release}>
                <polygon className={`rs-ar${Math.min(k, 3)}`} points={a.points} />
                <polyline className="rs-arl" points={a.points.split(' ').slice(0, n).join(' ')} />
              </g>
            ))}
            {markers.map(({ r, i }) => (
              <g key={r.release}>
                <line className="rs-mkl" x1={x(i)} x2={x(i)} y1={T} y2={H - B} />
                <line className="mk" x1={x(i)} x2={x(i)} y1={T - 6} y2={T} />
                <text className="rs-mkt" x={x(i) - 4} y={T - 3} textAnchor="end">
                  {versionBuild(r.release).version}
                </text>
              </g>
            ))}
            <line className="base" x1={L} x2={W - R} y1={H - B} y2={H - B} />
            {xLabels.map((i, k) => (
              <text
                key={i}
                className="ax"
                x={x(i)}
                y={H - 7}
                textAnchor={k === 0 ? 'start' : k === xLabels.length - 1 ? 'end' : 'middle'}
              >
                {dayLabel(i)}
              </text>
            ))}
            {labels.map((m) => (
              <text key={m.release} className="end-t" x={W - R + 8} y={m.ly + 3.5}>
                {`${versionBuild(m.release).version} `}
                <tspan className="rs-endv">{percentSpaced(i18n, m.end, 0)}</tspan>
              </text>
            ))}
          </svg>
        )}
      </div>
      {!pending && !empty ? (
        <div className="legend" translate="no">
          {list.map((r, k) => (
            <span key={r.release}>
              <i className={`rs-l${Math.min(k, 3)}`} />
              {r.release}
            </span>
          ))}
        </div>
      ) : null}
    </Section>
  )
}

/** The candidate's symbols in a few lines: the state, the images that miss them, how to upload. */
function SymbolsSummary({
  projectId,
  release,
  appId,
  app,
}: {
  projectId: string
  release: string
  appId: string | undefined
  app: App | undefined
}) {
  const i18n = useI18n()
  const { t, relative } = i18n
  const { orgId } = useProjectParams()
  const q = useQuery(missingSymbolsQuery(projectId, release, appId))
  const tabs = app ? uploadTabs(i18n, new Set([app.platform])) : []
  return (
    <Section
      title={t('releases.symbols')}
      meta={<span translate="no">{release}</span>}
      right={
        <Link
          className="btn ghost sm"
          to="/o/$orgId/p/$projectId/settings/$section"
          params={{ orgId, projectId, section: 'symbols' }}
        >
          {t('releases.symbols.settings')}
        </Link>
      }
    >
      <div className="rs-sym">
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} retry={() => q.refetch()} />
        ) : q.data.images === 0 ? (
          <p className="note">
            {t('releases.symbols.none')} {t('releases.symbols.noneBody')}
          </p>
        ) : q.data.missing.length === 0 ? (
          <p>
            <Status s="ok" label={t('releases.symbols.all', { images: q.data.images })} />
          </p>
        ) : (
          <>
            <p>
              <Status
                s="miss"
                label={t('releases.symbols.missing', {
                  missing: q.data.missing.length,
                  images: q.data.images,
                })}
              />
            </p>
            <ul className="rl">
              {q.data.missing.slice(0, 4).map((m) => (
                <li key={`${m.app_id}-${m.debug_id}`}>
                  <span>
                    <b translate="no">{m.image_name || t('symbols.unnamed')}</b>{' '}
                    <span className="faint">
                      {`· ${t(KIND_KEY[shownKind(m.kind, app?.platform)])} `}
                      <span translate="no">{`· ${m.arch}`}</span>
                    </span>
                  </span>
                  <span>{relative(m.last_seen)}</span>
                </li>
              ))}
            </ul>
            {tabs.length > 0 ? (
              // The command alone, as the mock's short window; the release's page has the notes.
              <CodeTabs
                label={t('releases.symbols.upload')}
                tabs={tabs.map((x) => ({ id: x.id, label: x.label, code: x.copy, copy: x.copy }))}
              />
            ) : null}
            <p className="note">
              <Link
                className="ul"
                to="/o/$orgId/p/$projectId/releases/$release"
                params={{ orgId, projectId, release }}
                search={appId ? { app: appId } : {}}
                hash="symbols"
              >
                {t('releases.symbols.needs')}
              </Link>
            </p>
          </>
        )}
      </div>
    </Section>
  )
}

/** Releases whose symbols the table shows, newest first; older rows say nothing. */
const SYMBOL_ROWS = 10
/** At most this many symbol reads at once, for the whole table. */
const symbolsQueue = new RunQueue(4)

/** Every release in the window, newest first: a row opens the release. */
function AllReleases({
  list,
  appId,
  android,
  share,
  device,
}: {
  list: ReleaseHealth[]
  appId: string | undefined
  android: boolean
  share: ReadonlyMap<string, number>
  device: DeviceParams
}) {
  const i18n = useI18n()
  const { t, date } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const params = { orgId, projectId }
  const scope = appId ? { app: appId } : {}
  // Launch and hangs per release: one read for the app's releases (APS-196).
  const vitals = useQuery({
    ...appVitalsQuery(projectId, appId ?? '', device),
    enabled: !!appId,
  })
  const vitalsBy = new Map((vitals.data ?? []).map((v) => [v.release, v]))
  // Symbols of the latest releases: one read each, a few at a time, never holding the table.
  const symbols = useQueries({
    queries: list.slice(0, SYMBOL_ROWS).map((r) => {
      const q = missingSymbolsQuery(projectId, r.release, appId)
      return {
        ...q,
        queryFn: (ctx: QueryFunctionContext) =>
          symbolsQueue.run(
            () => (q.queryFn as (c: QueryFunctionContext) => Promise<MissingSymbolsPage>)(ctx),
            ctx.signal,
          ),
      }
    }),
  })
  return (
    <Section
      title={t('releases.all')}
      meta={t('releases.all.sub', { days: WINDOW_DAYS })}
      right={
        <Link
          className="btn ghost sm"
          to="/o/$orgId/p/$projectId/issues"
          params={params}
          search={scope}
        >
          {t('releases.allIssues')}
        </Link>
      }
    >
      <RowKeysHint />
      <Table label={t('releases.all.table')} rowKeys className="rs-all">
        <thead>
          <tr>
            <th className="f" scope="col">
              {t('releases.col.release')}
            </th>
            <th scope="col">{t('releases.col.shipped')}</th>
            <th scope="col" title={t('releases.col.adoptionTitle')}>
              {t('releases.col.adoption')}
            </th>
            <th className="num" scope="col">
              {t('releases.col.sessions')}
            </th>
            <th className="num" scope="col">
              {t('releases.users')}
            </th>
            <th className="num" scope="col">
              {t('releases.col.crashFreeSessions')}
            </th>
            <th className="num" scope="col">
              {t('releases.col.crashFreeUsers')}
            </th>
            <th className="num" scope="col">
              {t('releases.col.launch')}
            </th>
            <th className="num" scope="col">
              {android ? t('releases.col.anrs') : t('releases.col.hangs')}
            </th>
            <th className="num" scope="col">
              {t('releases.col.newIssues')}
            </th>
            <th className="last" scope="col">
              {t('releases.col.symbols')}
            </th>
          </tr>
        </thead>
        <tbody>
          {list.map((r, i) => {
            const v = vitalsBy.get(r.release)
            const rate = v ? (android ? v.hangs.anr_rate : v.hangs.hang_rate) : null
            const s = share.get(r.release) ?? 0
            const open = () =>
              navigate({
                to: '/o/$orgId/p/$projectId/releases/$release',
                params: { ...params, release: r.release },
                search: scope,
              })
            return (
              <tr
                key={r.release}
                className="hov"
                onClick={(e) => {
                  if (!(e.target as HTMLElement).closest('a, button')) open()
                }}
              >
                <td className="f">
                  <Link
                    className="ttl"
                    to="/o/$orgId/p/$projectId/releases/$release"
                    params={{ ...params, release: r.release }}
                    search={scope}
                    translate="no"
                  >
                    {r.release}
                  </Link>
                  {i === 0 ? <span className="rs-new">{t('releases.rail.latest')}</span> : null}
                </td>
                <td>{date(r.released_at)}</td>
                <td>
                  <Hbar share={s} />
                  {percentSpaced(i18n, s, 0)}
                </td>
                <td className="num">{compact(i18n, r.sessions)}</td>
                <td className="num">{r.users === null ? '—' : compact(i18n, r.users)}</td>
                <td className="num">{percentSpaced(i18n, r.crash_free_sessions_rate, 2)}</td>
                <td className="num">
                  {r.crash_free_users_rate === null
                    ? '—'
                    : percentSpaced(i18n, r.crash_free_users_rate, 2)}
                </td>
                <td className="num">
                  {v?.app_start.cold ? duration(i18n, v.app_start.cold.p90_ms) : '—'}
                </td>
                <td className="num">
                  {rate === null ? (
                    '—'
                  ) : (
                    <>
                      {i18n.number(rate * 1000, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      <span className="faint" translate="no">
                        /1k
                      </span>
                    </>
                  )}
                </td>
                <td className="num">
                  {r.new_issues > 0 ? (
                    <Link
                      to="/o/$orgId/p/$projectId/issues"
                      params={params}
                      search={{ ...scope, release: r.release, tab: 'new' }}
                      aria-label={t('releases.newIssues.open', {
                        count: r.new_issues,
                        release: r.release,
                      })}
                    >
                      {i18n.number(r.new_issues)}
                    </Link>
                  ) : (
                    <span className="faint">{i18n.number(0)}</span>
                  )}
                </td>
                <td className="last">
                  <SymbolsCell state={symbols[i]} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </Section>
  )
}

/** A release's symbols in one word: ready, how many images miss them, or not known. */
export function SymbolsCell({ state }: { state: UseQueryResult<MissingSymbolsPage> | undefined }) {
  const { t } = useI18n()
  if (!state?.data || state.data.images === 0) return <span className="faint">—</span>
  return state.data.missing.length === 0 ? (
    <Status s="res" label={t('releases.symbols.ready')} />
  ) : (
    <Status
      s="miss"
      label={t('releases.symbols.missingShort', { count: state.data.missing.length })}
    />
  )
}
